import {Injectable} from '@angular/core';

import {EMPTY, defer, interval, merge, Observable, Subject, Subscription} from 'rxjs';
import {delay, retryWhen, switchMap, takeUntil, tap} from 'rxjs/operators';
import {webSocket, WebSocketSubject} from 'rxjs/webSocket';

import {Destroyable} from '../../destroyable.component';
import {NotificationFromSocket} from '../../inbox/shared/communications.interface';
import {AuthenticationEvent, AuthenticationService} from '../authentication';

import {ApiService} from './api.service';

type NotificationSocketMessage = NotificationFromSocket<any> | { type: string };

/**
 * Resilient WebSocket connection to the shared notification endpoint.
 * Reconnects automatically (with a fresh token) on error, close or heartbeat staleness, and exposes a single
 * long-lived `messages$` stream so consumers never need to re-subscribe when the underlying socket is rebuilt.
 */
@Injectable({
  providedIn: 'root'
})
export class NotificationSocketService extends Destroyable {

  private static PING_INTERVAL = 10 * 1000; // every 10s
  private static RETRY_INTERVAL = 2 * 1000; // every 2s
  private static WS_NOTIFICATION_PATH = '/communication/ws/notification';

  private messages: Subject<NotificationFromSocket<any>> = new Subject<NotificationFromSocket<any>>();
  private lastPingTime = 0;
  private lastPongTime = 0;
  private connectionSubscription: Subscription;

  constructor(private authenticationService: AuthenticationService,
              private api: ApiService) {
    super();

    this.connectionSubscription = this.webSocket$().subscribe();

    this.authenticationService.authenticationEvents.subscribe((value: { event: AuthenticationEvent }) => {
      if (value.event === AuthenticationEvent.LOGOUT) {
        this.closeSocket();
      }
    });

    window.addEventListener('beforeunload', () => this.closeSocket());
  }

  public messages$(): Observable<NotificationFromSocket<any>> {
    return this.messages.asObservable();
  }

  private closeSocket(): void {
    if (this.connectionSubscription) {
      this.connectionSubscription.unsubscribe();
    }
  }

  private webSocket$(): Observable<NotificationSocketMessage | number> {
    return defer(() => this.authenticationService.getToken())
      .pipe(
        switchMap((token: string) => {
          if (!token) {
            return EMPTY;
          }
          this.lastPingTime = 0;
          this.lastPongTime = 0;
          const socket = this.openSocket(token);
          return merge(
            socket.pipe(
              tap((message: NotificationSocketMessage) => {
                if ('type' in message && message.type === 'PONG') {
                  this.lastPongTime = new Date().getTime();
                } else {
                  this.messages.next(message as NotificationFromSocket<any>);
                }
              })
            ),
            this.heartbeat$(socket)
          );
        }),
        retryWhen((errors) => errors.pipe(delay(NotificationSocketService.RETRY_INTERVAL))),
        takeUntil(this.componentDestroyed$)
      );
  }

  private openSocket(token: string): WebSocketSubject<NotificationSocketMessage> {
    const wsApiHost = (this.api.apiHost || window.location.origin).replace('http', 'ws');
    return webSocket<NotificationSocketMessage>({
      url: `${wsApiHost}${NotificationSocketService.WS_NOTIFICATION_PATH}`,
      protocol: token
    });
  }

  private heartbeat$(socket: WebSocketSubject<NotificationSocketMessage>): Observable<number> {
    return interval(NotificationSocketService.PING_INTERVAL)
      .pipe(
        tap(() => {
          const lastPingPongInterval = this.lastPongTime - this.lastPingTime;
          if (lastPingPongInterval > NotificationSocketService.PING_INTERVAL || lastPingPongInterval < 0) {
            throw new Error('Notification socket heartbeat: no PONG received within the ping window');
          }
        }),
        tap(() => {
          this.lastPingTime = new Date().getTime();
          socket.next({type: 'PING'});
        })
      );
  }
}
